import React, { Fragment, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import Button from 'reactstrap/lib/Button';
import { get } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { getTaxCreditAmountPath, IncentiveModel } from 'client/data/models/incentives';
import { FeatureSpecsPaths, VehicleModel } from 'client/data/models/vehicle-v2';
import { InventoryTrimSummaryEntities } from 'client/data/models/inventory-core-configurations';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getEspLabel, getSuggestedDisplayPrice } from 'site-modules/shared/utils/core-page/suggested-price';
import { getFinalAssemblyCountry } from 'site-modules/shared/utils/features-specs/utils';
import { getDealsUrl } from 'site-modules/shared/utils/core-link-constructor';
import { withTargetText } from 'site-modules/shared/utils/url-utils';
import { getEspTrustDateUnformatted } from 'site-modules/shared/utils/esp';
import { INCENTIVE_TYPES } from 'site-modules/shared/constants/incentives';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { Link } from 'site-modules/shared/components/link/link';

const CREATIVE_ID = { CTA: 'edm-entry-model-summary-cta', SUMMARY: 'edm-entry-model-summary' };
const TOOLTIP_VALUE_MAP = { 'ev-tax-rebate': 'Federal EV Tax Credit', 'market-price': 'Edmunds Market Price' };

function TrimsCtaSimpleSummaryUI({
  className,
  selectedTrim,
  taxRebateAmount,
  finalAssemblyCountry,
  stateCode,
  vehicleName,
  vehicleParams,
  ctaProps,
  showLeadForm,
  showTaxRebates,
  isDealerlessOem,
  core6776Recipe,
  isInternational,
}) {
  const onTooltipOpen = useCallback(
    ({ id }) =>
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          subaction_name: TrackingConstant.VIEW_TOOLTIP,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          creative_id: CREATIVE_ID.SUMMARY,
          value: TOOLTIP_VALUE_MAP[id],
        },
      }),
    []
  );

  const ctaText = get(ctaProps, 'children');
  useEffect(() => {
    if (!ctaText) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'trim_summary_cta_text',
        creative_id: CREATIVE_ID.CTA,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: ctaText,
      },
    });
  }, [ctaText]);

  const finalAssemblyCountryText = finalAssemblyCountry && `Final assembly in ${finalAssemblyCountry}`;
  useEffect(() => {
    if (!finalAssemblyCountryText) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'country_of_final_assembly',
        creative_id: CREATIVE_ID.SUMMARY,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: finalAssemblyCountryText,
      },
    });
  }, [finalAssemblyCountryText]);

  useEffect(() => {
    if (taxRebateAmount === null) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: 'federal_ev_tax_credit',
        creative_id: CREATIVE_ID.SUMMARY,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        value: taxRebateAmount ? `Up to ${getPriceString(taxRebateAmount)}` : 'not eligible',
      },
    });
  }, [taxRebateAmount]);

  const totalMsrp = get(selectedTrim, 'pricing.totalMsrp');
  const baseMsrp = get(selectedTrim, 'pricing.baseMsrp');
  const ESP = get(selectedTrim, 'pricing.suggestedPrice');
  const nationalBasePriceTmv = isInternational ? get(selectedTrim, 'pricing.nationalBasePrice.tmv') : null;
  const displayPrice = getSuggestedDisplayPrice({ nationalBasePriceTmv, suggestedPrice: ESP, totalMsrp, baseMsrp });
  const espLabel = getEspLabel({ suggestedPrice: ESP, totalMsrp, nationalBasePriceTmv, core6776Recipe });

  const totalInventory = get(selectedTrim, 'inventoryCount');
  const buttonProps = {
    ...ctaProps,
    size: 'lg',
    color: 'blue-50',
    className: 'cta-button rounded-8 w-100 py-0_75 text-transform-none text-white',
    'data-hidedelightedoninteraction': true,
  };

  const evTaxRebateTooltip = (
    <TooltipItem
      id="ev-tax-rebate"
      className="top-0"
      iconClassName="small text-primary-darker"
      innerClassName="p-1"
      item={{
        placement: 'bottom',
        text: taxRebateAmount ? (
          <Fragment>
            Tax credit eligibility subject to income limits.{' '}
            <Link
              to={withTargetText(
                getDealsUrl(vehicleParams, {
                  styleId: get(selectedTrim, 'styleId'),
                  section: INCENTIVE_TYPES.OTHER,
                }),
                { text: 'federal credit' }
              )}
              className="text-primary-darker"
              data-tracking-id="view_ev_tax_credit"
              target="_blank"
            >
              Learn more
            </Link>
          </Fragment>
        ) : (
          `The ${get(selectedTrim, 'trimName', '')} trim does not qualify for the Clean Vehicle Credit.`
        ),
      }}
      delay={{ show: 0, hide: 250 }}
      autohide={false}
      onTooltipOpen={onTooltipOpen}
    />
  );

  return (
    <div className={classnames('trim-simple-summary size-16 w-100', className)} data-nosnippet>
      <div className="px-0_25">
        <div className="d-flex flex-column align-items-center justify-content-between">
          <div className="fw-medium mb-0_25">{espLabel}</div>
          <strong className="heading-2 d-flex align-items-center">
            {getPriceString(displayPrice)}
            {!ESP && (
              <TooltipItem
                id="no-esp"
                className="top-0 ms-0_5"
                iconClassName="small text-primary-darker"
                innerClassName="p-1"
                item={{
                  placement: 'bottom',
                  text: `Edmunds does not currently have enough transaction data to determine a Suggested Price for the ${
                    vehicleParams.year
                  } ${vehicleParams.makeName} ${vehicleParams.modelName}.`,
                }}
              />
            )}
          </strong>
        </div>
        {showTaxRebates && (
          <Fragment>
            <div className="text-center mt-1 medium text-cool-gray-40">
              <span className="fw-bold">
                <i className="icon-power2 text-turquoise-30" aria-hidden /> Federal EV Tax Credit:
              </span>
              <span className="fw-medium">
                {taxRebateAmount !== null &&
                  (taxRebateAmount ? ` up to ${getPriceString(taxRebateAmount)}` : ' not eligible')}
              </span>
              <span className="text-nowrap">
                &nbsp;
                {evTaxRebateTooltip}
              </span>
            </div>
          </Fragment>
        )}
        {ESP > totalMsrp && (
          <div
            className={classnames('small text-cool-gray-40 text-center', {
              'mt-0_25': !showTaxRebates,
              'mt-1': showTaxRebates,
            })}
          >
            Low supply is pushing the market average above MSRP.
          </div>
        )}
      </div>
      {!!ctaProps && (
        <Fragment>
          {showLeadForm ? (
            <Fragment>
              <Button
                {...buttonProps}
                className={classnames('mt-2', buttonProps.className)}
                data-tracking-parent={CREATIVE_ID.CTA}
              />
              {!!totalInventory && (
                <div className="small fst-italic mt-0_25 text-blue-40">{totalInventory} for sale near you</div>
              )}
            </Fragment>
          ) : (
            <Fragment>
              {isDealerlessOem && (
                <div className="text-start text-gray-darker mb-1 mt-2">
                  Choose the trim, color, options, packages and more for your {vehicleName}.
                </div>
              )}
              <Button
                {...buttonProps}
                className={classnames(buttonProps.className, {
                  'mt-2': !isDealerlessOem,
                })}
              />
            </Fragment>
          )}
        </Fragment>
      )}
      <div
        className="medium mt-1 mb-0_5 text-center"
        data-tracking-id="click_tariff_info"
        data-tracking-value={finalAssemblyCountryText}
      >
        <div>
          Prices based on sales in {stateCode} thru {dateFormat(getEspTrustDateUnformatted(), 'm/d/yy')}
        </div>
        {!!finalAssemblyCountryText && <div>{finalAssemblyCountryText}</div>}
      </div>
    </div>
  );
}

TrimsCtaSimpleSummaryUI.propTypes = {
  className: PropTypes.string,
  ctaProps: PropTypes.shape({
    onClick: PropTypes.func,
    to: PropTypes.string,
    rel: PropTypes.string,
    tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    'data-tracking-id': PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  selectedTrim: InventoryTrimSummaryEntities.TrimSummary.isRequired,
  vehicleName: PropTypes.string.isRequired,
  vehicleParams: PropTypes.shape({
    makeSlug: PropTypes.string,
    makeName: PropTypes.string,
    modelSlug: PropTypes.string,
    modelName: PropTypes.string,
    submodelSlug: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  taxRebateAmount: PropTypes.number,
  finalAssemblyCountry: PropTypes.string,
  stateCode: PropTypes.string,
  srpLink: PropTypes.string,
  showLeadForm: PropTypes.bool,
  showTaxRebates: PropTypes.bool,
  isDealerlessOem: PropTypes.bool,
  isMobile: PropTypes.bool,
  core6776Recipe: PropTypes.string,
  isInternational: PropTypes.bool,
};

TrimsCtaSimpleSummaryUI.defaultProps = {
  className: null,
  srpLink: null,
  taxRebateAmount: null,
  finalAssemblyCountry: null,
  stateCode: null,
  ctaProps: null,
  showLeadForm: false,
  showTaxRebates: false,
  isDealerlessOem: false,
  isMobile: false,
  core6776Recipe: 'ctrl',
  isInternational: false,
};

export const propsAreEqual = (prevProps, { showTaxRebates, taxRebateAmount }) =>
  showTaxRebates && taxRebateAmount === null;
export const TrimsCtaSimpleSummary = connectToModel(React.memo(TrimsCtaSimpleSummaryUI, propsAreEqual), {
  taxRebateAmount: bindToPath(
    ({ selectedTrim, showTaxRebates }) => showTaxRebates && getTaxCreditAmountPath(selectedTrim.styleId),
    IncentiveModel
  ),
  finalAssemblyCountry: bindToPath(
    ({ vehicleParams: { makeSlug, modelSlug, submodelSlug, year } }) =>
      FeatureSpecsPaths.buildPartialFeaturesPath({ make: makeSlug, model: modelSlug, submodel: submodelSlug, year }),
    VehicleModel,
    (stylesFeatures, { selectedTrim }) =>
      getFinalAssemblyCountry(stylesFeatures.find(({ id }) => id === selectedTrim.styleId))
  ),
  stateCode: bindToPath('location.stateCode', VisitorModel),
});
